import { interval, firstValueFrom, timeout, catchError, of } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';
import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Component, NgModule } from '@angular/core';
import { Mutex, withTimeout } from 'async-mutex';
import * as i1 from 'ngx-indexed-db';
import { DBMode, NgxIndexedDBModule } from 'ngx-indexed-db';
import * as i1$1 from '@angular/common/http';
import { HttpClientModule } from '@angular/common/http';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';

/**
 * Hilfsklasse zum Erstellen von Pagings auf Basis
 * von lokalen Daten
 */
class PageHelper {
    /**
     * Erstellt einen Pagy Wrapper für die Daten
     * @param {number} currentPage
     * @param {any[]} objects
     * @param {number} pageSize
     * @return {any}
     */
    static createPagyContent(currentPage, objects, pageSize) {
        let count = objects.length;
        objects = objects.slice((+currentPage - 1) * pageSize, (+currentPage) * pageSize);
        return {
            data: objects,
            pagy: {
                count: count,
                page: +currentPage,
                last: Math.ceil(count / pageSize),
                items: count < pageSize ? count : pageSize
            }
        };
    }
}

/**
 * Hilfsklasse zum lokalen Suchen
 */
class SearchHelper {
    /**
     * Generierung einer SortierFunktion für den Cursor
     * @param { string | null | undefined} order_by
     * @return {SortFunction<T> | null}
     */
    static generateSortFunction(order_by) {
        if (order_by) {
            let orderFields = order_by.split(" ");
            let fieldToOrder = orderFields[0];
            if (orderFields[1] === "DESC") {
                return (a, b) => a[fieldToOrder] < b[fieldToOrder];
            }
            else {
                return (a, b) => a[fieldToOrder] > b[fieldToOrder];
            }
        }
        return null;
    }
    /**
     * Fügt einen neuen Datensatz sortiert in die bestehende Menge ein
     * @param {Array<T>} objects
     * @param {T} newElement
     * @param {SortFunction<T> | null} sortFunc
     */
    static sortedInsert(objects, newElement, sortFunc) {
        if (objects.length === 0 || sortFunc === null) {
            objects.push(newElement);
        }
        else {
            let i = 0;
            for (; i < objects.length; i++) {
                const element = objects[i];
                if (sortFunc(element, newElement)) {
                    break;
                }
            }
            let lastObjects = objects.splice(i, objects.length - i);
            objects.push(newElement);
            objects.push(...lastObjects);
        }
    }
}

/**
 * Eintrag in der Warteschlange
 */
class StoredData {
}

/**
 * Subscribed und löst die Subscription beim löschen
 */
class SubscriptionReady {
    constructor(timeInterval) {
        this.timer = interval(timeInterval);
    }
    /**
     * Subscription aufheben
     */
    clearSubscription() {
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = undefined;
        }
    }
    /**
     * Worker Loop beenden
     * @return {boolean}
     */
    get breakWorkerLoop() {
        return this.subscription === undefined;
    }
    /**
     * Subscription registrieren
     * @param {Subscription} subscription
     */
    registerSubscription(funcToSubscribe) {
        if (!this.subscription) {
            this.subscription = this.timer.subscribe(() => funcToSubscribe());
        }
    }
}

const FULL_TEXT_QUEUE = "fulltextQueue";
class FulltextQueueService {
    /**
     * Constructor
     * @param indexedDBService
     */
    constructor(indexedDBService) {
        this.indexedDBService = indexedDBService;
        this.mutex = new Mutex();
    }
    /**
     * Einfügen eines Eintrages in die Queue
     * @param {FullTextQueueEntry} entry
     * @return {Promise<FullTextQueueEntry | undefined>}
     */
    async add(entry) {
        const release = await this.mutex.acquire();
        let result = await firstValueFrom(this.indexedDBService.add(FULL_TEXT_QUEUE, entry));
        release();
        return result;
    }
    /**
     * Fügt mehrere Einträge auf einmal in die Queue ein
     * @param entries
     * @return {Promise<FullTextQueueEntry[] | undefined>}
     */
    async addAll(entries) {
        const release = await this.mutex.acquire();
        let result = await firstValueFrom(this.indexedDBService.bulkAdd(FULL_TEXT_QUEUE, entries));
        release();
        return result;
    }
    /**
     * Eintrag laden und aus der Queue entfernen
     * Gibt undefined zurück, wenn keine Daten mehr vorhanden sind
     * @return {Promise<FullTextQueueEntry | undefined>}
     */
    async pop() {
        const release = await this.mutex.acquire();
        return new Promise((resolve, reject) => {
            this.indexedDBService
                .openCursorByIndex(FULL_TEXT_QUEUE, "fullTextKey", IDBKeyRange.lowerBound(0))
                .subscribe(async (cursorWrapper) => {
                const cursor = cursorWrapper.target
                    .result;
                if (cursor) {
                    const value = cursor.value;
                    await this.delete(value.id);
                    release();
                    resolve(value);
                }
                else {
                    release();
                    resolve(undefined);
                }
            });
        });
    }
    /***
     * n Einträge aus Queue laden und danach entfernen
     */
    async popAll(n) {
        let values = [];
        return new Promise((resolve, reject) => {
            this.indexedDBService
                .openCursorByIndex(FULL_TEXT_QUEUE, "fullTextKey", IDBKeyRange.lowerBound(0), DBMode.readwrite)
                .subscribe(async (cursorWrapper) => {
                const cursor = cursorWrapper.target
                    .result;
                if (cursor) {
                    const value = cursor.value;
                    values.push(value);
                    cursor.delete();
                    if (values.length >= n) {
                        resolve(values);
                    }
                    else {
                        cursor.continue();
                    }
                }
                else {
                    resolve(values);
                }
            });
        });
    }
    /**
     * Löschen eines Queue Entries
     * @param {number} id
     * @return {Promise<void>}
     */
    async delete(id) {
        return firstValueFrom(this.indexedDBService.deleteByKey(FULL_TEXT_QUEUE, id));
    }
    /**
     * Anzahl der noch offenen Einträge
     * @return {Promise<number>}
     */
    async count() {
        return firstValueFrom(this.indexedDBService.count(FULL_TEXT_QUEUE));
    }
    /**
     * Löschen der Queue
     */
    clear() {
        return firstValueFrom(this.indexedDBService.clear(FULL_TEXT_QUEUE));
    }
}
FulltextQueueService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: FulltextQueueService, deps: [{ token: i1.NgxIndexedDBService }], target: i0.ɵɵFactoryTarget.Injectable });
FulltextQueueService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: FulltextQueueService, providedIn: "root" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: FulltextQueueService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root",
                }]
        }], ctorParameters: function () { return [{ type: i1.NgxIndexedDBService }]; } });

const FULLTEXT_WORKER_INTERVAL = 10 * 1000;
const POP_AMOUNT = 500;
/**
 * FullTextWerker
 * --------------
 * Aktualisiert im Hintergrund den Fulltextservice
 */
class FulltextWorker extends SubscriptionReady {
    /**
     * Constructor
     * @param indexedDBService
     */
    constructor(indexedDBService) {
        super(FULLTEXT_WORKER_INTERVAL);
        this.indexedDBService = indexedDBService;
        this.mutex = new Mutex();
        this.fulltextQueueService = new FulltextQueueService(indexedDBService);
    }
    /**
     * Instanz generieren da Singelton
     * @param {NgxIndexedDBService} indexedDBService
     * @return {FulltextWorker}
     */
    static getInstance(indexedDBService) {
        if (this.instance === undefined)
            this.instance = new FulltextWorker(indexedDBService);
        return this.instance;
    }
    /**
     * Getter FulltextQueueService
     * @return {FulltextQueueService}
     */
    get fulltextQueue() {
        return this.fulltextQueueService;
    }
    /**
     * Verarbeitung starten
     */
    start() {
        this.registerSubscription(() => this.internalProcess());
    }
    /**
     * Verarbeitung stoppen
     */
    stop() {
        this.clearSubscription();
    }
    get popAmount() {
        return POP_AMOUNT;
    }
    /**
     * Eintrag einfügen und verarbeiten
     */
    async internalProcess() {
        const release = await this.mutex.acquire();
        while ((await this.fulltextQueueService.count()) > 0 && !this.breakWorkerLoop) {
            const entries = await this.fulltextQueueService.popAll(this.popAmount);
            const entriesToInsert = await this.loadEntries(entries);
            let values = Object.values(entriesToInsert);
            for (const entry of values.filter((e) => e.persisted)) {
                await firstValueFrom(this.indexedDBService.update(FULL_TEXT_STORE, entry));
            }
            await this.addEntries(values.filter((e) => !e.persisted));
        }
        release();
    }
    /**
     * Add entries to indexdb with w3c apis
     */
    async addEntries(entries) {
        return new Promise((resolve, reject) => {
            const DBOpenRequest = window.indexedDB.open("vendingSync", 3);
            DBOpenRequest.onsuccess = (event) => {
                const db = event.target.result;
                const transaction = db.transaction([FULL_TEXT_STORE], "readwrite");
                transaction.oncomplete = () => resolve(true);
                transaction.onerror = (event) => reject(event);
                // create an object store on the transaction
                const objectStore = transaction.objectStore(FULL_TEXT_STORE);
                for (const newItem of entries) {
                    const objectStoreRequest = objectStore.add(newItem);
                }
                transaction.commit();
            };
        });
    }
    /**
     * Einträge laden/transformieren
     * @param entries
     * @returns
     */
    async loadEntries(entries) {
        const fulltextEntries = {};
        for (const entry of entries) {
            if (!fulltextEntries[entry.fullTextKey]) {
                const currentEntry = await this.loadEntry(entry.fullTextKey, entry.entityType);
                if (currentEntry !== undefined) {
                    fulltextEntries[entry.fullTextKey] = currentEntry;
                    fulltextEntries[entry.fullTextKey].persisted = true;
                }
                else {
                    fulltextEntries[entry.fullTextKey] = this.buildEntry(entry.fullTextKey, entry.entityId, entry.entityType);
                }
            }
            if (fulltextEntries[entry.fullTextKey]) {
                let fullTextEntry = fulltextEntries[entry.fullTextKey];
                const removeEntry = entry.entityId < 0;
                entry.entityId = Math.abs(entry.entityId);
                if (!fullTextEntry.references.includes(entry.entityId) && !removeEntry) {
                    fullTextEntry.references.push(entry.entityId);
                }
                else if (removeEntry) {
                    fullTextEntry.references = fullTextEntry.references.filter((ref) => ref !== entry.entityId);
                }
            }
        }
        return fulltextEntries;
    }
    async loadEntry(key, entityType) {
        return firstValueFrom(this.indexedDBService.getByKey(FULL_TEXT_STORE, [
            key,
            entityType,
        ]));
    }
    /**
     * Fügt einen Fulltexteintrag zur Datenbank hinzu
     * @param {string} key
     * @param {number} entityId
     * @param {string} entityType
     * @return {Promise<FullTextEntry|undefined>}
     */
    async insertIntoIndex(key, entityId, entityType) {
        console.debug("FulltextWorker insertIntoIndex", key, entityId, entityType);
        const removeEntry = entityId < 0;
        entityId = Math.abs(entityId);
        let fullTextEntry = await firstValueFrom(this.indexedDBService.getByKey(FULL_TEXT_STORE, [
            key,
            entityType,
        ]));
        let response;
        if (fullTextEntry) {
            if (!fullTextEntry.references.includes(entityId) && !removeEntry) {
                fullTextEntry.references.push(entityId);
                response = firstValueFrom(this.indexedDBService.update(FULL_TEXT_STORE, fullTextEntry));
            }
            else if (removeEntry) {
                fullTextEntry.references = fullTextEntry.references.filter((ref) => ref !== entityId);
                response = firstValueFrom(this.indexedDBService.update(FULL_TEXT_STORE, fullTextEntry));
            }
        }
        else if (!removeEntry) {
            fullTextEntry = this.buildEntry(key, entityId, entityType);
            response = firstValueFrom(this.indexedDBService.add(FULL_TEXT_STORE, fullTextEntry));
        }
        return response;
    }
    /**
     * Eintrag bauen
     * @param {string} key
     * @param {number} entityId
     * @param {string} entityType
     * @return {FullTextEntry}
     */
    buildEntry(key, entityId, entityType) {
        return {
            key: key,
            entityType: entityType,
            references: [entityId],
        };
    }
}

const FULL_TEXT_STORE = 'fulltext';
const STRING_SPLITTER = ' ';
class FullTextService {
    /**
     * Constructor
     * @param indexedDBService
     */
    constructor(indexedDBService) {
        this.indexedDBService = indexedDBService;
        this.minimalLetters = 3;
        this.mutex = new Mutex();
        this.fulltextWorker = FulltextWorker.getInstance(this.indexedDBService);
        this.fulltextWorker.start();
    }
    /**
     * Worker starten, sofern noch nicht gestartet
     */
    startWorker() {
        this.fulltextWorker.start();
    }
    /**
     * Worker beenden
     */
    stopWorker() {
        this.fulltextWorker.stop();
    }
    /**
     * Sind Einträge in der Fulltext Queue?
     * @return {Promise<boolean>}
     */
    async entriesEnqueued() {
        const count = await this.fulltextWorker.fulltextQueue.count();
        return count > 0;
    }
    /**
     * Sind Einträge in der Fulltext Queue?
     * @returns amount of entries in queue
     */
    async entriesEnqueuedAmount() {
        return await this.fulltextWorker.fulltextQueue.count();
    }
    /**
     * Sind Einträge in der Fulltext Queue?
     * @returns amount of entries in queue
     */
    async entriesIndexAmount() {
        return await firstValueFrom(this.indexedDBService.count(FULL_TEXT_STORE));
    }
    /**
     * Hinzufügen eines Eintrags zum VolltextIndex
     * @param {StoredData<any>} entry
     * @param {string[]} properties welche Attribute sollen zum Index eingefügt werden
     * @param {boolean} remove soll der Eintrag gelöscht werden?
     */
    async add(entry, properties, remove = false) {
        let release = await this.mutex.acquire();
        let promises = [];
        properties.forEach((property) => {
            let value = entry.content[property];
            if (value) {
                value = value.toLocaleLowerCase();
                let response = this.bulkInsertIntoWorkflowQueue(value, entry.entityId * (remove ? -1 : 1), entry.entityType);
                if (response)
                    promises.push(response);
            }
        });
        await Promise.all(promises);
        release();
    }
    /**
     * Auflisten aller VolltextIndex-Einträge eines Objektes
     * @param object to find
     */
    async get(object) {
        let release = await this.mutex.acquire();
        let storedFulltextEntries = await firstValueFrom(this.indexedDBService.getAll(FULL_TEXT_STORE));
        let fulltextEntries = storedFulltextEntries.filter((fulltextEntry) => fulltextEntry.references.includes(object.id));
        release();
        return fulltextEntries;
    }
    async bulkInsertIntoWorkflowQueue(value, entityId, entityType) {
        let keys = value
            .split(STRING_SPLITTER)
            .filter((v) => v.length >= this.minimalLetters);
        let keysToInsert = new Set();
        keys.forEach((key) => {
            for (let i = this.minimalLetters; i <= key.length; i++) {
                const element = key.substring(0, i);
                keysToInsert.add(element);
            }
        });
        let insertionKeys = Array.from(keysToInsert.keys()).map((key) => {
            return {
                fullTextKey: key,
                entityId: entityId,
                entityType: entityType,
            };
        });
        return await this.fulltextWorker.fulltextQueue.addAll(insertionKeys);
    }
    async insertIntoWorkflowQueue(key, entityId, entityType) {
        return this.fulltextWorker.fulltextQueue.add({
            fullTextKey: key,
            entityId: entityId,
            entityType: entityType,
        });
    }
    /**
     * Fügt einen Fulltexteintrag zur Datenbank hinzu
     * @param {string} key
     * @param {number} entityId
     * @param {string} entityType
     * @return {Promise<FullTextEntry|undefined>}
     */
    async insertIntoIndex(key, entityId, entityType) {
        return this.fulltextWorker.insertIntoIndex(key, entityId, entityType);
    }
    /**
     * Löschen des Volltext-Index
     */
    async clear() {
        return firstValueFrom(this.indexedDBService.clear(FULL_TEXT_STORE));
    }
    /**
     * Suche nach einem Suchstring und Rückgabe einer Ergebnismenge
     * @param {string} input
     * @param {string} type
     * @return {Promise<T[]>}
     */
    async search(input, type) {
        if (this.empty(input)) {
            return [];
        }
        else {
            let inputs = input
                .toLocaleLowerCase()
                .split(STRING_SPLITTER)
                .filter((i) => i.length >= this.minimalLetters);
            let promises = inputs.map((input) => this.indexdbSearch(input, type));
            let results = await Promise.all(promises);
            return this.evaluateResults(results);
        }
    }
    /**
     * Suche auf der internen Datenbank starten
     * @param {string} input
     * @param {string} type
     * @return {Promise<FullTextEntry>}
     */
    indexdbSearch(input, type) {
        return firstValueFrom(this.indexedDBService.getByKey(FULL_TEXT_STORE, [
            input,
            type,
        ]));
    }
    /**
     * Zwischenergebnisse auswerten und die Nummern zurückgeben
     * Wenn mehrere Einträge geprüft werden, wird überprüft das die Referenzen
     * eindeutig sind, d.h. UND-Verknüpfung
     * @param {FullTextEntry[]} results
     * @return {number[]}
     */
    evaluateResults(results) {
        if (results && results.length > 0) {
            let firstResult = results.shift();
            if (results.length >= 1) {
                let output = firstResult?.references.filter((reference) => {
                    return results
                        .filter((r) => r !== null && r !== undefined)
                        .map((r) => r.references.includes(reference))
                        .reduce((a, b) => a && b, true);
                });
                return output || [];
            }
            else {
                return firstResult?.references || [];
            }
        }
        else {
            return [];
        }
    }
    /**
     * Prüft ob ein Eingangsstring leer ist
     * @param {string | null | undefined} input
     * @return {boolean}
     */
    empty(input) {
        return input === null || input === undefined || input === '';
    }
}
FullTextService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: FullTextService, deps: [{ token: i1.NgxIndexedDBService }], target: i0.ɵɵFactoryTarget.Injectable });
FullTextService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: FullTextService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: FullTextService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.NgxIndexedDBService }]; } });

/**
 * Eintrag in der Warteschlange
 */
class QueueEntry {
    constructor() {
        this.ready = true;
    }
}

const STORAGE_KEY = 'SyncQueue';
class SyncQueueService {
    /**
     * Constructor
     * - type entspricht den Namen der Entity z.B. Customer. Darüber werden die Daten in der Queue wieder gefunden
     * @param {string} type
     */
    constructor(dbService, type) {
        // Sollen vorherige Queuedaten gelöscht werden?
        this._deletePreviousEntries = false;
        this.dbService = dbService;
        this.type = type;
    }
    set deletePreviousEntries(value) {
        this._deletePreviousEntries = value;
    }
    /**
     * Lädt die gespeicherten Daten
     * @return {Promise<T[]>}
     */
    async getQueuedData() {
        // TODO hier auf den Datensatz beschränken
        return firstValueFrom(this.dbService.getAll(STORAGE_KEY));
    }
    /**
     * Löschen einer Entity aus der Warteschlange
     * @param id
     * @returns
     */
    async deleteForEntity(id) {
        if (this.type === '' || id === undefined || id === null)
            return Promise.resolve(false);
        let elements = await this.getQueuedData();
        for (let element of elements) {
            if (element.entityId === id) {
                await this.delete(element.id);
            }
        }
        return true;
    }
    /**
     * Sind Daten für eine spezifische Entity hinterlegt?
     * @param {number} id
     * @return {Promise<boolean>}
     */
    async hasDataQueued(id) {
        let result = await firstValueFrom(this.dbService.getAllByIndex(STORAGE_KEY, 'entityType', IDBKeyRange.only(this.type)));
        let index = result.findIndex((entry) => entry.entityId === +id);
        return Promise.resolve(index >= 0);
    }
    /**
     * Object in die Warteschlange hängen
     * Gibt den technischen Primärschlüssel für den Datensatz zurück
     * @param {T} object
     * @return {Promise<number>}
     */
    async add(object) {
        const queueEntry = new QueueEntry();
        queueEntry.content = object;
        queueEntry.createdAt = new Date();
        queueEntry.entityId = object.id;
        queueEntry.entityType = this.type;
        await this.deleteEntriesForEntity(queueEntry.entityId);
        const result = await firstValueFrom(this.dbService.add(STORAGE_KEY, queueEntry));
        return result.id;
    }
    /**
     * Löscht lokale Daten zu einem Eintrag
     * @param id
     * @returns
     */
    async deleteEntriesForEntity(id) {
        if (!this._deletePreviousEntries)
            return Promise.resolve(false);
        let queuedEntries = await firstValueFrom(this.dbService.getAllByIndex(STORAGE_KEY, 'entityType', IDBKeyRange.only(this.type)));
        if (queuedEntries && queuedEntries.length > 0) {
            queuedEntries = queuedEntries.filter((entry) => entry.entityId === +id);
            if (queuedEntries.length > 0) {
                for (let entry of queuedEntries) {
                    await firstValueFrom(this.dbService.deleteByKey(STORAGE_KEY, entry.id));
                }
            }
        }
        return Promise.resolve(true);
    }
    /**
     * Object as der Warteschlange lesen
     * @param {number} id des Eintrags
     * @return {Promise<QueueEntry<T>>} den Eintrag
     */
    async get(id) {
        return await firstValueFrom(this.dbService.getByID(STORAGE_KEY, id));
    }
    /**
     * Object in die Warteschlange hängen
     * Gibt den technischen Primärschlüssel für den Datensatz zurück
     * @param {QueueEntry<T>} queueEntry
     * @return {Promise<number>}
     */
    async update(queueEntry) {
        const result = await firstValueFrom(this.dbService.update(STORAGE_KEY, queueEntry));
        return result.id;
    }
    /**
     * Object aus Queue mit dem technischen Primärschlüssel löschen
     * @param {number} id
     * @return {Promise<boolean>}
     */
    async delete(id) {
        return firstValueFrom(this.dbService.deleteByKey(STORAGE_KEY, id));
    }
    /**
     * Leert die Queue
     * @return {Promise<boolean>}
     */
    clear() {
        return firstValueFrom(this.dbService.clear(STORAGE_KEY));
    }
}

const OBJECT_SYNC_STORAGE = 'objects';
const CONFIGURABLE_ID_FIELD = 'configurableId';
const ENTITY_TYPE_INDEX = 'entityType';
const DEFAULT_PAGE_SIZE = 25;
/**
 * Basisklasse für Modelle zum Synchronisieren
 * leitet die Methode find, all, where auf die lokalen varianten um.
 */
class SyncService {
    /**
     * Default-Constructor
     * @param {NgxIndexedDBService} indexedDBService
     * @param {SyncProcessorService} syncProcessor
     * @param {string} type
     */
    constructor(indexedDBService, syncProcessor, type, configurableIdField = undefined) {
        this.indexedDBService = indexedDBService;
        this.syncProcessor = syncProcessor;
        this.type = type;
        this.configurableIdField = configurableIdField;
        if (this.type != null) {
            this.syncProcessor.register(type, this);
        }
        this.fulltextService = new FullTextService(this.indexedDBService);
    }
    /**
     * Ruft ein Objekt mit Details ab
     * @param {number} id
     * @return {Observable<T>}
     */
    findRemote(id) {
        throw 'NotImplemented';
    }
    /**
     * Volltextsuche für eine Zeichenkette ausführen
     * @param {string} input
     * @return {Promise<T[]>}
     */
    async fullTextSearch(input) {
        let ids = await this.fulltextService.search(input, this.type);
        if (ids.length > 0) {
            let storedObjects = await Promise.all(ids.map((id) => this.localFind(id)));
            return storedObjects
                .filter((obj) => obj !== null && obj !== undefined)
                .map((obj) => obj.content);
        }
        else {
            return [];
        }
    }
    /**
     * Synchronisiert einen Datensatz in den lokalen bestand.
     * Sollte er serverseitig gelöscht worden sein, wird dieser auch lokal entfernt
     * @param {SyncInformation} info
     * @return {Promise<boolean>}
     */
    async sync(info) {
        try {
            if (info.deleted_at) {
                await this.localDelete(info.entity_id);
            }
            else {
                let result = await firstValueFrom(this.findRemote(info.entity_id));
                await this.store(result, info.updated_at);
            }
            return Promise.resolve(true);
        }
        catch (error) {
            console.error(error);
            return Promise.resolve(true);
        }
    }
    /**
     * Zählt die Anzahl der Datensätze im lokalen Datenbestand
     * @param onlyOwnType - nur Datensätze des eigenen Typs zählen
     * @return {Promise<number>}
     */
    async localCount(onlyOwnType) {
        return firstValueFrom(this.indexedDBService.countByIndex(OBJECT_SYNC_STORAGE, ENTITY_TYPE_INDEX, onlyOwnType ? this.type : undefined));
    }
    /**
     * Gibt alle Datensätze der Entity T zurück
     * @return {Observable<T[]>}
     */
    localAll() {
        return this.localAllStoredData().pipe(map((entries) => entries.map((entry) => entry.content)));
    }
    /**
     * Gibt alle Datensätze der Entity T zurück
     * @return {Observable<StoredData<T>[]>}
     */
    localAllStoredData() {
        return this.indexedDBService.getAllByIndex(OBJECT_SYNC_STORAGE, ENTITY_TYPE_INDEX, IDBKeyRange.bound(this.type, this.type));
    }
    /**
     * Suche in der Indexeddb
     * param args[search] -> Volltextsuche
     * param args[*] -> spezifische Suche nach attribut
     * @param {Record<string,any>} args
     * @return {Promise<ResponseModel<T>>}
     */
    async localWhere(args) {
        let page = args['page'] || 1;
        let search = args['search'] || '';
        let orderBy = args['order_by'];
        search = atob(search);
        delete args['page'];
        delete args['search'];
        delete args['order_by'];
        let sortFunc = SearchHelper.generateSortFunction(orderBy);
        return new Promise((resolve, reject) => {
            let objects = [];
            this.executeWithCursor((cursorWrapper) => {
                let cursor = cursorWrapper.target
                    .result;
                if (cursor) {
                    let element = cursor.value;
                    if (this.compareArgs(args, element.content) &&
                        this.compareSearch(element.content, search)) {
                        SearchHelper.sortedInsert(objects, element.content, sortFunc);
                    }
                    cursor.continue();
                }
                else {
                    resolve(PageHelper.createPagyContent(page, objects, DEFAULT_PAGE_SIZE));
                }
            }, (error) => reject(error));
        });
    }
    /**
     * Durchsucht die lokale Datenbank und gibt die ersten X einträge wieder die zu der Suche passend sind und bricht dann ab.
     *
     * @param {Record<string,any>} args
     * @param {number} amount
     * @return {Promise<T[]>}
     */
    async localFastSearch(args, amount) {
        let search = args['search'] || '';
        let orderBy = args['order_by'];
        search = atob(search);
        delete args['page'];
        delete args['search'];
        delete args['order_by'];
        let sortFunc = SearchHelper.generateSortFunction(orderBy);
        return new Promise((resolve, reject) => {
            let objects = [];
            this.executeWithCursor((cursorWrapper) => {
                let cursor = cursorWrapper.target
                    .result;
                if (cursor && objects.length <= amount) {
                    let element = cursor.value;
                    if (this.compareArgs(args, element.content) &&
                        this.compareSearch(element.content, search)) {
                        SearchHelper.sortedInsert(objects, element.content, sortFunc);
                    }
                    cursor.continue();
                }
                else {
                    resolve(objects);
                }
            }, (error) => reject(error));
        });
    }
    /**
     * Suche in der Indexeddb
     * param args[search] -> Volltextsuche
     * param args[*] -> spezifische Suche nach attribut
     * @param {Record<string,any>} args
     * @return {Promise<ResponseModel<T>>}
     */
    async localWhereWithoutPaging(args) {
        const amount = await this.localCount();
        return this.localFastSearch(args, amount - 1);
    }
    async getFulltextAmount() {
        return {
            queued: await this.fulltextService.entriesEnqueuedAmount(),
            finished: await this.fulltextService.entriesIndexAmount(),
        };
    }
    /**
     * Suche im Objekt
     * @param {Object} content
     * @param {string} search
     * @return {boolean}
     */
    compareSearch(content, search) {
        if (!search)
            return true;
        if (search === '')
            return true;
        return JSON.stringify(content)
            .toLocaleLowerCase()
            .match(`: ?"?[a-zA-Z0-9-_ .&äüö]*${search.toLocaleLowerCase()}`) !== null;
    }
    /**
     * Argumente vergleichen mit dem Objekt
     * @param {Record<string,any>} args
     * @param {Record<string,any>} content
     * @return {boolean}
     */
    compareArgs(args, content) {
        if (!args)
            return true;
        if (Object.keys(args).length === 0)
            return true;
        return Object.keys(args)
            .map((key) => {
            try {
                let argValue = args[key];
                let contentValue = content[key];
                if (Array.isArray(argValue) && !Array.isArray(contentValue)) {
                    // Eingabe ist Array und content ist Wert
                    // -> Es wird erwartet das der Wert im Array ist.
                    return args[key].includes(contentValue);
                }
                else if (!Array.isArray(argValue) && Array.isArray(contentValue)) {
                    // Eingabe ist Wert und Content ist Array
                    // -> Es wird erwartet das die Eingabe im content enthalten ist
                    return contentValue.includes(argValue);
                }
                else if (Array.isArray(argValue) && Array.isArray(contentValue)) {
                    // Beides ein Array
                    // Es wird erwartet, das Array Eingabe vollständig in Array Content enthalten ist
                    let isObjectArray = argValue.every((a) => typeof a === 'object');
                    // Fall 1: Werte sind Objekte
                    if (isObjectArray) {
                        return (argValue.filter((arg) => {
                            return Object.keys(arg)
                                .map((key) => contentValue
                                .map((content) => arg[key] === content[key])
                                .reduce((a, b) => a || b))
                                .reduce((a, b) => a && b);
                        }).length > 0);
                    }
                    else {
                        // Fall 2: Werte sind Basiswerte
                        return (argValue.filter((x) => {
                            return contentValue.includes(x);
                        }).length > 0);
                    }
                }
                else {
                    return args[key] === contentValue;
                }
            }
            catch (ex) {
                return false;
            }
        })
            .reduce((pv, cv) => pv && cv);
    }
    /**
     * Abrufen der lokalen Daten mit Paging
     * @param {number} page Default Value ist 1
     * @return {Promise<ResponseModel<T>>}
     */
    async localAllwithPaging(page = 1) {
        // Anzahl der Datensätze
        let count = await this.localCount();
        let hasSkipped = false;
        return new Promise((resolve, reject) => {
            let objects = [];
            this.executeWithCursor((cursorWrapper) => {
                let cursor = cursorWrapper.target
                    .result;
                if (!hasSkipped && page > 1) {
                    cursor.advance((page - 1) * DEFAULT_PAGE_SIZE);
                }
                if (cursor && objects.length < DEFAULT_PAGE_SIZE) {
                    let element = cursor.value;
                    objects.push(element.content);
                    cursor.continue();
                }
                else {
                    let lastPage = Math.ceil(+count / DEFAULT_PAGE_SIZE);
                    resolve({
                        data: objects,
                        pagy: {
                            count: count,
                            page: page,
                            last: lastPage,
                            items: count < DEFAULT_PAGE_SIZE ? count : DEFAULT_PAGE_SIZE,
                        },
                    });
                }
            }, (error) => reject(error));
        });
    }
    /**
     * Führt einen Cursor auf die lokalen Daten auf
     * @param {(event: Event) => void} success
     * @param {(error: any) => void } reject
     */
    executeWithCursor(success, reject) {
        this.indexedDBService
            .openCursorByIndex(OBJECT_SYNC_STORAGE, ENTITY_TYPE_INDEX, IDBKeyRange.bound(this.type, this.type))
            .subscribe(success, reject);
    }
    /**
     * Erstellet einen storedData Entry
     * @param {T} obj
     * @return {StoredData<T>}
     */
    createStoredData(obj) {
        let entry = new StoredData();
        entry.content = obj;
        entry.entityId = obj.id;
        entry.entityType = this.type;
        entry.updatedAt = obj.updated_at;
        this.addConfigurableId(entry);
        return entry;
    }
    /**
     * Fügt die konfigurierbare ID zum Entry hinzu
     * @param {StoredData<T>} entry
     */
    addConfigurableId(entry) {
        if (this.configurableIdField !== undefined) {
            entry.configurableId = entry.content[this.configurableIdField];
        }
    }
    /**
     * Speichert einen StoredData Entry
     * @param {StoredData<T>} entry
     * @return {Promise<boolean|StoredData<T>|undefined>}
     */
    async storeData(entry) {
        return this.storeInternal(entry);
    }
    /**
     * Speichert die Daten in dem Store weg
     * @param {T} obj
     * @param {string} updatedAt
     */
    async store(obj, updatedAt) {
        let entry = new StoredData();
        entry.content = obj;
        entry.entityId = obj.id;
        entry.entityType = this.type;
        entry.updatedAt = updatedAt;
        this.addConfigurableId(entry);
        return this.storeInternal(entry);
    }
    /**
     * Speichern eines Datensatzes intern
     * @param {StoredData<T>} entry
     * @return {Promise<boolean|StoredData<T>|undefined>}
     */
    async storeInternal(entry) {
        let alreadyStored = await this.localFind(entry.entityId);
        if (alreadyStored &&
            new Date(alreadyStored.updatedAt) > new Date(entry.updatedAt)) {
            return Promise.resolve(false);
        }
        else {
            return firstValueFrom(this.indexedDBService.update(OBJECT_SYNC_STORAGE, entry));
        }
    }
    /**
     * Daten in dem lokalen Datenbestand finden
     * @param {number} id
     * @return {Promise<StoredData<T>>}
     */
    async localFind(id) {
        return firstValueFrom(this.indexedDBService.getByKey(OBJECT_SYNC_STORAGE, this.buildIndex(id))).then((result) => result);
    }
    /**
     * Daten in dem lokalen Datenbestand mit konfigurierbaren ID finden
     * @param {string | number} alternativeId
     * @return {Promise<StoredData<T>>}
     */
    async localFindByConfigurableId(alternativeId) {
        return firstValueFrom(this.indexedDBService.getByIndex(OBJECT_SYNC_STORAGE, CONFIGURABLE_ID_FIELD, this.buildIndex(alternativeId, false)));
    }
    /**
     * Sind Fehler für einen fehler vorhanden?
     * @param {number} id
     * @return {Promise<SyncError | undefined>}
     */
    async hasError(id) {
        let data = await this.localFind(id);
        return Promise.resolve(data.errors);
    }
    /**
     * Daten aus dem lokalen Datenbestand löschen
     * @param {number} id
     * @return {Promise<any>}
     */
    async localDelete(id) {
        return firstValueFrom(this.indexedDBService.deleteByKey(OBJECT_SYNC_STORAGE, this.buildIndex(id)));
    }
    /**
     * Local Löschen und löschen aus der lokalen Queue
     * Es wird kein Server-Request ausgelöst
     * @param id
     * @returns
     */
    async deleteWithQueue(id) {
        const service = new SyncQueueService(this.indexedDBService, this.type);
        const result = await service.deleteForEntity(id);
        await this.localDelete(id);
        return result;
    }
    /**
     * Erstellt den Index zum suchens
     * @param {string|number} id
     * @param {boolean} force Transformation in number erzwingen
     * @return {Array<string|number>}
     */
    buildIndex(id, force = true) {
        if (force)
            id = +id;
        return [id, this.type];
    }
}

const TIMESTAMP_KEY = "SyncInformationService.lastTime";
const TIMEOUT = 30 * 1000;
class SyncInformationService {
    /**
     * Default-Constructor
     * @param {HttpClient} http
     * @param {ErrorService} errorService
     */
    constructor(http) {
        this.http = http;
    }
    /**
     * Erhält alle Objecte vom Endpunkt.
     * @return {Promise<SyncInformation[]> | null}
     */
    async all() {
        return firstValueFrom(this.http.get(this.endpointWithUrl).pipe(timeout(TIMEOUT), catchError((e) => {
            // do something on a timeout
            return of(null);
        }))).then((result) => {
            return result;
        });
    }
    /**
     * Erhält alle Objekte mit den gefilterten Argumenten
     * @param args to append to the url
     */
    async where(args) {
        return firstValueFrom(this.http.get(this.endpointWithUrl, { params: args })
            .pipe(timeout(TIMEOUT), catchError((e) => {
            // do something on a timeout
            return of(null);
        }))).then((result) => {
            return result;
        });
    }
    /**
     * Handelt es sich um den initalen Sync
     * @return {boolean}
     */
    get isInitalSync() {
        return this.lastTimestamp === null;
    }
    /**
     * letzten gespeicherten Zeitstempel abrufen (UNIX-Zeit)
     * @return {number | null}
     */
    get lastTimestamp() {
        let lastTime = parseInt(localStorage.getItem(TIMESTAMP_KEY) || "");
        return lastTime > 0 ? lastTime : null;
    }
    /**
     * Setter lastTimestamp
     * @param {number} v
     */
    set lastTimestamp(v) {
        localStorage.setItem(TIMESTAMP_KEY, v ? v.toString() : "");
    }
    /**
     * Timestamp entfernen
     */
    clearTimestamp() {
        localStorage.removeItem(TIMESTAMP_KEY);
    }
    /**
     * Endpoint URL
     * @return {string}
     */
    get endpointWithUrl() {
        return `${SyncInformationService.getServiceUrl()}/sync_engine/sync/index?last_time=${this.lastTimestamp || ""}`;
    }
    /**
     * Setzt die ServiceUrl
     * @param {string} url
     */
    static setServiceUrl(url) {
        if (this.serviceUrl !== url) {
            this.serviceUrl = url;
        }
    }
    /**
     * Getter ServiceUrl
     * @return {string}
     */
    static getServiceUrl() {
        return this.serviceUrl;
    }
}
SyncInformationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: SyncInformationService, deps: [{ token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
SyncInformationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: SyncInformationService, providedIn: "root" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: SyncInformationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root",
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }]; } });

/**
 * Grundlegende Operationen auf Basis der IndexDB
 * z.B. gezieltes Löschen und wiederherstellen der IndexDB
 */
class SyncEngineService {
    constructor(syncService, indexedDBService) {
        this.syncService = syncService;
        this.indexedDBService = indexedDBService;
    }
    /**
     * Löscht die komplette IndexDB und den Zeitstempel zum Synchronisieren
     * @return {Promise<boolean>}
     */
    async resetAll() {
        let result = await firstValueFrom(this.indexedDBService.deleteDatabase());
        if (result) {
            this.syncService.clearTimestamp();
        }
        return result;
    }
}
SyncEngineService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: SyncEngineService, deps: [{ token: SyncInformationService }, { token: i1.NgxIndexedDBService }], target: i0.ɵɵFactoryTarget.Injectable });
SyncEngineService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: SyncEngineService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: SyncEngineService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: SyncInformationService }, { type: i1.NgxIndexedDBService }]; } });

// Default TIMEOUT 5 Minuten
const MUTEX_TIMEOUT = 5 * 60 * 1000;
/**
 * Verarbeitet eine bestehende Queue und bietet Methoden zum Synchronisieren an
 */
class OfflineQueueProcessorService {
    /**
     * Default-Constructor
     * @param {number} syncInterval
     * @param {SyncQueueService<T>} syncQueue
     * @param {(obj: T) => Promise<boolean>} syncFunction
     */
    constructor(syncInterval, syncQueue, syncFunction, type, syncService, mutexTimeout = MUTEX_TIMEOUT) {
        this.type = type;
        this.syncService = syncService;
        this.syncStarted = false;
        this.syncQueue = syncQueue;
        this.syncInterval = syncInterval;
        this.syncFunction = syncFunction;
        this.mutex = withTimeout(new Mutex(), mutexTimeout);
    }
    /**
     * Object in die Warteschlange hängen
     * @param {T} object
     * @return {Promise<number>}
     */
    async queue(object) {
        const result = await this.syncQueue.add(object);
        return result;
    }
    /**
     * Object aus Queue löschen
     * @param {number} id
     * @return {Promise<boolean>}
     */
    async delete(id) {
        return this.syncQueue.delete(id);
    }
    /**
     * Starte die Verarbeitung der Daten
     */
    startSync() {
        if (!this.syncStarted) {
            this.syncStarted = true;
            interval(this.syncInterval)
                .pipe(takeWhile(() => this.syncStarted))
                .subscribe(() => this.internalSync());
        }
    }
    /**
     * Sind Daten für eine spezifische Entity hinterlegt?
     * @param {number} id
     * @return {Promise<boolean>}
     */
    async hasDataQueued(id) {
        return this.syncQueue.hasDataQueued(id);
    }
    /**
     * Synchronisation anhalten
     */
    stopSync() {
        this.syncStarted = false;
    }
    /**
     * Lädt die Einträge aus der Warteschlange
     * @return {Promise<QueueEntry<T>[]>}
     */
    async getQueuedData() {
        return this.syncQueue.getQueuedData();
    }
    /**
     * Lädt einen Eintrag aus der Warteschlange nach Queue-ID
     * @return {Promise<QueueEntry<T>>}
     */
    async getQueuedId(id) {
        return this.syncQueue.get(id);
    }
    /**
     * Lädt einen Eintrag aus der Warteschlange nach Queue-ID
     * @return {Promise<number>} Idntifier des Eintrags
     */
    async updateQueued(queueEntry) {
        return this.syncQueue.update(queueEntry);
    }
    /**
     * Synchronisation nur einmal ausführen
     */
    runSyncOnce() {
        this.internalSync();
    }
    /**
     * Interne Synchronisierungslogik
     * -----
     * Mutex wird benötigt, damit der Prozess auch wirklich nur einmal läuft
     * und kein doppelter Versand erfolgt.
     */
    internalSync() {
        try {
            if (window.navigator.onLine) {
                this.mutex
                    .acquire()
                    .then((release) => {
                    this.processObjectsSync()
                        .then((result) => {
                        release();
                    })
                        .catch((error) => {
                        console.error('Verarbeitung bei Daten abgebrochen');
                        console.error(error);
                        release();
                    });
                })
                    .catch((error) => {
                    this.mutex.cancel();
                    console.error('Error while Mutex acquire');
                    console.error(error);
                });
            }
        }
        catch (error) {
            console.error(error);
        }
    }
    /**
     * Speichern in dem normalen Objekt
     * @param {QueueEntry<T>} obj
     * @param {SyncError}
     */
    async storeError(obj, error) {
        let data = this.syncService.createStoredData(obj.content);
        data.errors = error;
        return this.syncService.storeData(data);
    }
    /**
     * Fehler verarbeiten
     * @param {QueueEntry<T>} obj
     * @param {SyncError} error
     * @return {Promise<boolean>}
     */
    async handleError(obj, error) {
        if (error.shouldThrowFromQueue) {
            await this.storeError(obj, error);
            await this.internalDelete(obj);
            return Promise.resolve(true);
        }
        else if (error.shouldNotSendAgain) {
            obj.error = error;
            obj.ready = false;
            await this.syncQueue.update(obj);
            return Promise.resolve(false);
        }
        else {
            return Promise.resolve(false);
        }
    }
    /**
     * Löschen mit Ausgabe
     * @param {QueueEntry<T>} obj
     */
    internalDelete(obj) {
        const removeResult = this.delete(obj.id);
        return removeResult.then((value) => {
            return value;
        });
    }
    /**
     * Lokales Objekt löschen, wenn es kein bestehender Datensatz war.
     * @param {any} obj
     * @return {Promise<boolean>}
     */
    async tryToDeleteLocalObject(obj) {
        if (obj.entityId < 0) {
            // Es handelt sich um ein temporäres Objekt
            await this.syncService.localDelete(obj.entityId);
        }
        return Promise.resolve(true);
    }
    /**
     * Bilder zum Upload verarbeiten
     * @return {Promise<boolean>}
     */
    async processObjectsSync() {
        const objects = await this.getQueuedData();
        const promises = [];
        objects
            .filter((obj) => obj.entityType === this.type)
            .forEach((obj) => {
            if (obj.ready) {
                console.debug(`Processing Object: ${obj.entityId} from type ${obj.entityType}`);
                promises.push(this.syncFunction(obj.content)
                    .then((result) => {
                    if (result) {
                        this.internalDelete(obj);
                        this.tryToDeleteLocalObject(obj)
                            .then((result) => {
                            console.debug(`Lokales Objekt gelöscht ${obj.entityType} ${obj.entityId}`);
                        })
                            .catch((error) => {
                            console.debug(`Konnte lokales objekt nicht löschen ${obj.entityType} ${obj.entityId}}`);
                        });
                    }
                    return result;
                })
                    .catch((error) => {
                    return this.handleError(obj, error);
                }));
            }
            else {
                console.debug('Object not ready: ' + obj.id);
                if (obj.error)
                    console.debug(obj.error);
            }
        });
        let result = [];
        try {
            result = await Promise.all(promises);
        }
        catch (error) {
            console.error(error);
        }
        return result.reduce((pv, cv) => pv && cv, true);
    }
}

class SyncProcessorService {
    /**
     * Constructor
     * @param {SyncInformationService} syncInformation
     */
    constructor(syncInformation) {
        this.syncInformation = syncInformation;
        this.services = {};
        this.mutex = new Mutex();
        /**
         * Initialer Sync wurde gestartet
         */
        this.fullSyncStarted = new EventEmitter();
        /**
         * Initialer Sync Fortschritt in Prozent
         */
        this.fullSyncProgess = new EventEmitter();
        /**
         * Initialer Sync wurde gestopped
         */
        this.fullSyncStopped = new EventEmitter();
        /**
         * Sync wurde gestartet
         */
        this.syncStarted = new EventEmitter();
        /**
         * Sync Fortschritt in Prozent
         */
        this.syncProgess = new EventEmitter();
        /**
         * Sync wurde gestopped
         */
        this.syncStopped = new EventEmitter();
    }
    /**
     * Get registered Services
     * @return {Record<string, ISyncService>}
     */
    get registeredServices() {
        return this.services;
    }
    /**
     * Letzte Synchronisierung am
     * @return {Date|null}
     */
    get lastSyncTimestamp() {
        let ts = this.syncInformation.lastTimestamp;
        if (ts) {
            return new Date(ts);
        }
        else {
            return null;
        }
    }
    /**
     * Verarbeitung der eingehenden Daten-Synchronisierung starten
     * @param args Filter Argumente für den Request der Syncinformationen aus dem Backend
     */
    async process(args) {
        let release = await this.mutex.acquire();
        let initalSync = this.syncInformation.isInitalSync;
        if (initalSync)
            this.fullSyncStarted.emit();
        else
            this.syncStarted.emit();
        let syncStarted = this.currentTimestamp;
        let result = args
            ? await this.syncInformation.where(args)
            : await this.syncInformation.all();
        if (result == null) {
            release();
            return;
        }
        let promises = [];
        let promisesBatch = [];
        for (let i = 0; i < result.length; i++) {
            const object = result[i];
            let service = this.services[object.entity_type];
            if (service) {
                let syncResult = service.sync(object).then((loadedObject) => { });
                promises.push(syncResult);
                promisesBatch.push(syncResult);
            }
            if (promisesBatch.length == 10) {
                const progress = (i / result.length) * 100;
                if (initalSync)
                    this.fullSyncProgess.emit(progress);
                else
                    this.syncProgess.emit(progress);
                await Promise.all(promisesBatch);
                promisesBatch.splice(0, promisesBatch.length);
            }
        }
        return Promise.all(promises)
            .then((result) => {
            if (initalSync)
                this.fullSyncStopped.emit();
            else
                this.syncStopped.emit();
            this.syncInformation.lastTimestamp = syncStarted;
            release();
            return result;
        })
            .catch((error) => {
            console.error(error);
            release();
            return error;
        });
    }
    /**
     * Aktuellen Zeitstempel. Uhrzeit
     * @return {number}
     */
    get currentTimestamp() {
        return Date.now();
    }
    /**
     * Registriert einen Dienst für einen spezifischen Typ
     * @param {string} type
     * @param {ISyncService} service
     */
    register(type, service) {
        this.services[type] = service;
    }
}
SyncProcessorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: SyncProcessorService, deps: [{ token: SyncInformationService }], target: i0.ɵɵFactoryTarget.Injectable });
SyncProcessorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: SyncProcessorService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: SyncProcessorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: SyncInformationService }]; } });

const REFRESH_INTERVAL = 10 * 1000;
class FulltextCounterComponent extends SubscriptionReady {
    constructor(fullTextQueueService) {
        super(REFRESH_INTERVAL);
        this.fullTextQueueService = fullTextQueueService;
        this.count = 0;
    }
    ngOnInit() {
        this.registerSubscription(() => {
            this.updateCounter();
        });
    }
    ngOnDestroy() {
        this.clearSubscription();
    }
    /**
     * Zähler aktualisieren
     */
    async updateCounter() {
        this.count = await this.fullTextQueueService.count();
    }
}
FulltextCounterComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: FulltextCounterComponent, deps: [{ token: FulltextQueueService }], target: i0.ɵɵFactoryTarget.Component });
FulltextCounterComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.5", type: FulltextCounterComponent, selector: "lib-fulltext-counter", usesInheritance: true, ngImport: i0, template: "<p *ngIf=\"count > 0\"> \n    In Bearbeitung <strong>{{count}}</strong>\n</p>\n<p *ngIf=\"count <= 0\">\n    Abgeschlossen\n</p>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: FulltextCounterComponent, decorators: [{
            type: Component,
            args: [{ selector: "lib-fulltext-counter", template: "<p *ngIf=\"count > 0\"> \n    In Bearbeitung <strong>{{count}}</strong>\n</p>\n<p *ngIf=\"count <= 0\">\n    Abgeschlossen\n</p>\n" }]
        }], ctorParameters: function () { return [{ type: FulltextQueueService }]; } });

/**
 * Konfiguration für den Bildstore
 */
const dbConfig = {
    name: "vendingSync",
    version: 3,
    objectStoresMeta: [
        {
            store: STORAGE_KEY,
            storeConfig: { keyPath: "id", autoIncrement: true },
            storeSchema: [
                { name: "entityId", keypath: "entityId", options: { unique: false } },
                {
                    name: "entityType",
                    keypath: "entityType",
                    options: { unique: false },
                },
                { name: "content", keypath: "content", options: { unique: false } },
                { name: "createdAt", keypath: "createdAt", options: { unique: false } },
            ],
        },
        {
            store: OBJECT_SYNC_STORAGE,
            storeConfig: {
                keyPath: ["entityId", "entityType"],
                autoIncrement: false,
            },
            storeSchema: [
                { name: "entityId", keypath: "entityId", options: { unique: false } },
                {
                    name: "entityType",
                    keypath: "entityType",
                    options: { unique: false },
                },
                {
                    name: CONFIGURABLE_ID_FIELD,
                    keypath: [CONFIGURABLE_ID_FIELD, "entityType"],
                    options: { unique: false },
                },
                { name: "content", keypath: "content", options: { unique: false } },
                { name: "updatedAt", keypath: "createdAt", options: { unique: false } },
            ],
        },
        {
            store: FULL_TEXT_STORE,
            storeConfig: { keyPath: ["key", "entityType"], autoIncrement: false },
            storeSchema: [
                { name: "key", keypath: "key", options: { unique: false } },
                {
                    name: "entityType",
                    keypath: "entityType",
                    options: { unique: false },
                },
                {
                    name: "references",
                    keypath: "references",
                    options: { unique: false },
                },
            ],
        },
        {
            store: FULL_TEXT_QUEUE,
            storeConfig: { keyPath: "id", autoIncrement: true },
            storeSchema: [
                {
                    name: "fullTextKey",
                    keypath: "fullTextKey",
                    options: { unique: false },
                },
                {
                    name: "entityId",
                    keypath: "entityId",
                    options: { unique: false },
                },
                {
                    name: "entityType",
                    keypath: "entityType",
                    options: { unique: false },
                },
            ],
        },
    ],
};
class SyncEngineClientModule {
}
SyncEngineClientModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: SyncEngineClientModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SyncEngineClientModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.5", ngImport: i0, type: SyncEngineClientModule, declarations: [FulltextCounterComponent], imports: [CommonModule,
        HttpClientModule, i1.NgxIndexedDBModule], exports: [FulltextCounterComponent] });
SyncEngineClientModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: SyncEngineClientModule, providers: [SyncProcessorService, FullTextService], imports: [CommonModule,
        HttpClientModule,
        NgxIndexedDBModule.forRoot(dbConfig)] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: SyncEngineClientModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        HttpClientModule,
                        NgxIndexedDBModule.forRoot(dbConfig),
                    ],
                    providers: [SyncProcessorService, FullTextService],
                    exports: [FulltextCounterComponent],
                    declarations: [
                        FulltextCounterComponent
                    ],
                }]
        }] });

const REMOVEABLE_HTTP_STATUS = [200, 201, 204];
const SHOULD_NOT_SEND_AGAIN = [422, 500, 501, 503, 507, 508, 509, 510, 511];
/**
 * Fehlermeldung aus dem Backend (Rails)
 */
class SyncError {
    constructor(httpStatus, errors) {
        this.httpStatus = httpStatus;
        this.errors = errors;
    }
    /**
     * Soll der Eintrag von der Queue weggeworfen werden?
     * @return {boolean}
     */
    get shouldThrowFromQueue() {
        return REMOVEABLE_HTTP_STATUS.includes(this.httpStatus);
    }
    /**
     * Soll der Eintrag nicht automatisch nochmal gesendet werden?
     * @return {boolean}
     */
    get shouldNotSendAgain() {
        return SHOULD_NOT_SEND_AGAIN.includes(this.httpStatus);
    }
}

/*
 * Public API Surface of sync-engine-client
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CONFIGURABLE_ID_FIELD, FulltextCounterComponent, OBJECT_SYNC_STORAGE, OfflineQueueProcessorService, STORAGE_KEY, SyncEngineClientModule, SyncEngineService, SyncError, SyncInformationService, SyncProcessorService, SyncQueueService, SyncService, dbConfig };
